import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import DeleteIcon from '@mui/icons-material/Delete';
import { memo, useCallback, type SyntheticEvent } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Button,
  Accordion,
  IconButton,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { InputField } from 'components';

import { Fields } from './fields';
import { SortableBlockAccordionProps } from './types';
import { CopyPasteActions } from './copy-paste-actions';
import { getBlockItemId, getNonScalingTransform } from './dnd';

const stopAccordionToggle = (event: SyntheticEvent) => {
  event.stopPropagation();
};

export const SortableBlockAccordion = memo(
  ({
    index,
    block,
    blockId,
    expanded,
    onRemove,
    onToggle,
    simplified,
    blocksName,
    allowInputId,
    enableSorting,
    allowOutcomes,
    onCopySection,
    canDeleteBlocks,
    allowOutcomeFields,
    allowReferenceCodes,
    onPasteSectionAfter,
    outcomeFieldSources,
  }: SortableBlockAccordionProps) => {
    const sortable = useSortable({
      disabled: !enableSorting,
      id: getBlockItemId(blockId),
    });
    const dragTransform = getNonScalingTransform(sortable.transform);
    const handleRemove = useCallback(() => {
      onRemove(index);
    }, [index, onRemove]);
    const handleToggle = useCallback(
      (nextExpanded: boolean) => {
        onToggle(blockId, nextExpanded);
      },
      [blockId, onToggle]
    );

    return (
      <Accordion
        square
        disableGutters
        expanded={expanded}
        ref={enableSorting ? sortable.setNodeRef : undefined}
        onChange={(_, nextExpanded) => handleToggle(nextExpanded)}
        sx={{
          '&.Mui-expanded': {
            margin: 0,
          },
          '&::before': {
            display: 'none',
          },
        }}
        style={{
          transition: sortable.transition,
          transform: CSS.Transform.toString(dragTransform),
          opacity: enableSorting && sortable.isDragging ? 0.45 : 1,
        }}
      >
        <AccordionSummary
          component="div"
          expandIcon={simplified ? null : <ArrowDownwardIcon />}
          sx={{
            minHeight: 0,
            '&.Mui-expanded': {
              minHeight: 0,
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '&.Mui-expanded .MuiAccordionSummary-content': {
              margin: 0,
            },
          }}
        >
          <div className="mr-4 flex w-full items-center justify-between gap-3 py-2">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {enableSorting && (
                <IconButton
                  size="small"
                  aria-label="Drag section"
                  onClick={(event) => event.stopPropagation()}
                  className="cursor-grab border border-gray-300"
                  {...sortable.attributes}
                  {...sortable.listeners}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              )}

              <div
                className="min-w-0 flex-1"
                onClick={stopAccordionToggle}
                onKeyDown={stopAccordionToggle}
                onMouseDown={stopAccordionToggle}
                onFocusCapture={stopAccordionToggle}
              >
                {simplified ? (
                  <Typography variant="subtitle2">
                    {block.name?.trim() || 'Untitled section'}
                  </Typography>
                ) : (
                  <InputField
                    required={false}
                    label="Section name (optional)"
                    placeholder="Enter section name"
                    name={`${blocksName}.${index}.name`}
                  />
                )}
              </div>
            </div>

            {!simplified && (
              <div className="flex items-center gap-2">
                <CopyPasteActions
                  stopPropagation
                  entityLabel="section"
                  onCopy={() => onCopySection(index)}
                  onPasteAfter={() => onPasteSectionAfter(index)}
                />
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={!canDeleteBlocks}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemove();
                  }}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
        </AccordionSummary>

        {(simplified || expanded) && (
          <AccordionDetails className="flex flex-col gap-4">
            {simplified && (
              <Typography variant="caption" color="textSecondary">
                Drag fields by handle to reorder or move between sections.
              </Typography>
            )}

            <Fields
              fields={block.fields}
              simplified={simplified}
              allowInputId={allowInputId}
              enableSorting={enableSorting}
              allowOutcomes={allowOutcomes}
              name={`${blocksName}.${index}.fields`}
              allowOutcomeFields={allowOutcomeFields}
              allowReferenceCodes={allowReferenceCodes}
              outcomeFieldSources={outcomeFieldSources}
            />
          </AccordionDetails>
        )}
      </Accordion>
    );
  }
);

SortableBlockAccordion.displayName = 'SortableBlockAccordion';
